import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookingModal } from "@/components/BookingModal";
import { 
  CheckCircle2, MessageSquare, Phone, Bot, Calendar, Users, 
  TrendingUp, LayoutTemplate, Globe, Star, Mail, Workflow, 
  BarChart, Smartphone, MapPin, ArrowRight 
} from "lucide-react";

const services = [
  { name: "Missed Call Text Back", desc: "Instantly send an automatic text message to every missed caller, letting them know you'll be in touch quickly.", icon: MessageSquare },
  { name: "AI Voice Receptionist", desc: "Answer your business calls 24/7 with an intelligent AI receptionist that can answer questions, qualify leads, schedule appointments, and transfer calls.", icon: Phone },
  { name: "AI Chat Assistant", desc: "Turn your website into a lead-generating machine with an AI chatbot that answers customer questions day and night.", icon: Bot },
  { name: "Online Appointment Booking", desc: "Allow customers to book appointments online anytime with automated reminders that reduce no-shows.", icon: Calendar },
  { name: "CRM & Customer Management", desc: "Keep every customer organized in one easy-to-use dashboard with complete communication history, notes, appointments, and opportunities.", icon: Users },
  { name: "Pipeline & Sales Management", desc: "Track every lead from first contact to completed sale using customizable sales pipelines.", icon: TrendingUp },
  { name: "Lead Capture Forms", desc: "Collect leads directly from your website, landing pages, social media, and advertising campaigns.", icon: LayoutTemplate },
  { name: "Landing Page Design", desc: "Professionally designed landing pages built to convert visitors into paying customers.", icon: LayoutTemplate },
  { name: "Website Builder", desc: "Modern, mobile-friendly websites designed to help Edmonton businesses rank higher on Google and generate more leads.", icon: Globe },
  { name: "Reputation Management", desc: "Automatically request Google reviews from satisfied customers and monitor your online reputation from one dashboard.", icon: Star },
  { name: "Two-Way Business Text Messaging", desc: "Communicate with customers by SMS from your business number while keeping every conversation organized.", icon: MessageSquare },
  { name: "Email Marketing", desc: "Create and automate professional email campaigns that keep customers engaged and encourage repeat business.", icon: Mail },
  { name: "Marketing Automation", desc: "Automate follow-up messages, appointment reminders, review requests, customer nurturing, and much more.", icon: Workflow },
  { name: "Workflow Automation", desc: "Replace repetitive manual tasks with automated workflows that save time and increase productivity.", icon: Workflow },
  { name: "Call Tracking", desc: "Monitor incoming calls, measure marketing performance, and understand which advertising generates the best results.", icon: Phone },
  { name: "Call Recording", desc: "Review customer conversations to improve service quality and staff training.", icon: Phone },
  { name: "Business Phone System", desc: "Professional business phone solutions with call forwarding, voicemail, extensions, and advanced call routing.", icon: Phone },
  { name: "Voicemail Drop", desc: "Leave pre-recorded voicemail messages without ringing your customer's phone.", icon: Phone },
  { name: "Ringless Text & SMS Campaigns", desc: "Reach customers with personalized text message campaigns that generate more responses.", icon: MessageSquare },
  { name: "Email & SMS Campaigns", desc: "Send targeted marketing campaigns to the right audience at the right time.", icon: Mail },
  { name: "Customer Database Management", desc: "Store, organize, and manage all customer information securely in one central location.", icon: Users },
  { name: "Sales Funnels", desc: "Create high-converting sales funnels designed to generate leads and increase revenue.", icon: TrendingUp },
  { name: "Membership Websites", desc: "Build secure membership areas for courses, training, exclusive content, or customer resources.", icon: Globe },
  { name: "Online Courses", desc: "Sell educational content with a complete online learning platform.", icon: LayoutTemplate },
  { name: "Invoicing & Payment Collection", desc: "Create invoices and securely collect customer payments online.", icon: TrendingUp },
  { name: "Social Media Scheduling", desc: "Schedule and publish content across multiple social media platforms from one dashboard.", icon: Globe },
  { name: "Google Business Profile Messaging", desc: "Respond to customer inquiries quickly and professionally while improving local engagement.", icon: MapPin },
  { name: "Customer Review Automation", desc: "Increase your online reputation by automatically requesting reviews after every completed job.", icon: Star },
  { name: "Internal Team Chat", desc: "Keep your staff connected with built-in communication tools.", icon: Users },
  { name: "Mobile Business App", desc: "Manage your business from anywhere using your smartphone or tablet.", icon: Smartphone },
  { name: "Reporting & Analytics", desc: "Track leads, sales, phone calls, appointments, campaign performance, and business growth with easy-to-understand reports.", icon: BarChart },
  { name: "AI-Powered Customer Follow-Up", desc: "Automatically follow up with new leads until they respond, helping increase conversions without extra work.", icon: Bot },
  { name: "Local SEO Solutions", desc: "Help your Edmonton business improve visibility in local Google searches and attract more qualified customers.", icon: MapPin },
];

const benefits = [
  "Increase lead conversions",
  "Never miss another customer opportunity",
  "Save hours every week through automation",
  "Improve customer communication",
  "Grow your online reputation",
  "Increase appointments and sales",
  "Mobile-friendly business management",
  "Local Edmonton support",
  "Solutions customized for your business",
];

const AIServices = () => {
  return (
    <Layout>
      <SEO
        title="AI Solutions Edmonton | Business Automation & AI Solutions | Missed Call Rescue"
        description="AI Services Edmonton - Complete business automation solutions for small businesses. Missed Call Rescue provides AI receptionist, automated text back, workflow automation & conversational AI for Edmonton businesses."
        canonical="/ai-services"
        keywords="AI services Edmonton, business automation Edmonton, AI solutions Alberta, small business AI tools, automated text back Edmonton, AI receptionist Canada, marketing automation Edmonton"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Missed Call Rescue - AI Services Edmonton",
            url: "https://missedcallrescuecanada.com/ai-services",
            telephone: ["1-855-780-3522", "780-237-6933"],
            email: "missedcallrescuecanada@gmail.com",
            areaServed: "Edmonton, Alberta, Canada",
            description: "Complete business automation services for Edmonton small businesses including AI voice receptionist, automated text back, workflow automation, CRM, and marketing automation.",
            priceRange: "$$",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What AI services does Missed Call Rescue offer for Edmonton businesses?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We offer AI voice receptionist, AI chat assistant, automated text back, online appointment booking, CRM and customer management, workflow automation, marketing automation, reputation management, call tracking, and local SEO solutions for small businesses in Edmonton.",
                },
              },
              {
                "@type": "Question",
                name: "Can the AI receptionist schedule appointments for my business?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Your AI receptionist will book, reschedule, cancel, manage waitlists, and confirm appointments across phone calls, SMS, website chat, and automated workflows.",
                },
              },
              {
                "@type": "Question",
                name: "What industries does Missed Call Rescue serve in Edmonton?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We serve contractors, dentists, lawyers, realtors, automotive shops, medical clinics, salons, restaurants, HVAC services, plumbers, roofers, autobody shops, mechanics, hair salons, veterinary clinics, property management companies, home care agencies, mortgage brokers, med spas, and many other service-based businesses.",
                },
              },
              {
                "@type": "Question",
                name: "How can automation help my Edmonton small business?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Automation helps you capture more leads, increase sales, improve customer communication, save hours every week, grow your online reputation, and increase appointments — all while you focus on running your business.",
                },
              },
            ],
          },
        ]}
      />
      {/* Hero Section */}
      <section className="bg-white text-primary pt-24 pb-16 md:pt-32 md:pb-24 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
AI Solutions Edmonton Complete Business Automation & AI Solutions for Small Businesses
          </h1>
          <p className="text-xl md:text-2xl text-primary/90 mb-8 font-light">
            Grow Your Business with Powerful AI & Marketing Automation
          </p>
          <div className="text-lg text-primary/80 mb-10 max-w-3xl mx-auto">
            At Missed Call Rescue, we help Edmonton businesses capture more leads, increase sales, improve customer communication, and automate everyday tasks so you can focus on running your business. 
            Whether you're a contractor, dentist, lawyer, realtor, automotive shop, medical clinic, salon, restaurant, or any service-based business, we provide the tools you need to grow faster while delivering an exceptional customer experience.
            <br />
            <br />
            <span className="italic font-bold text-primary">"Work Smarter not Harder" Scale without burning out!</span>
          </div>
          <BookingModal>
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer">
              Get Your Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </BookingModal>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Our Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to automate, optimize, and scale your Edmonton business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="p-6 border-slate-200 hover:border-primary/30 transition-colors shadow-sm hover:shadow-md bg-white flex flex-col h-full">
                <div className="h-12 w-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-4 shrink-0">
                  <service.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{service.name}</h3>
                <p className="text-slate-600 text-sm flex-grow">{service.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Why Choose Missed Call Rescue?</h2>
              <p className="text-lg text-slate-600 mb-8">
                We are dedicated to helping Edmonton's local businesses thrive by providing cutting-edge AI and automation solutions that actually deliver results.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-secondary shrink-0 mr-3" />
                    <span className="text-slate-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-secondary/10 rounded-3xl blur-3xl" />
<picture>
                <source srcSet="https://wsrv.nl/?url=https%3A%2F%2Fvibe.filesafe.space%2F1776204073728798439%2Fattachments%2F23f1bfb2-3b51-419d-919b-df574cb73161.png&output=webp" type="image/webp" />
                <img 
                  src="https://vibe.filesafe.space/1776204073728798439/attachments/23f1bfb2-3b51-419d-919b-df574cb73161.png" 
alt="AI Services Edmonton — Missed Call Rescue AI voice agent answers every call instantly, books appointments, and captures more jobs for Edmonton small businesses while roofers, plumbers, and contractors are busy on the job" 
                  className="relative z-10 w-full h-auto object-contain rounded-3xl shadow-xl border border-border/50"
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={1000}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white text-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-secondary rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-accent rounded-full opacity-10 blur-3xl"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Grow Your Business?</h2>
          <p className="text-xl text-primary/80 mb-10 max-w-3xl mx-auto">
            If you're looking for an affordable all-in-one business automation solution in Edmonton, Missed Call Rescue will help. Contact us today for a free consultation and discover how our powerful automation tools can help you attract more customers, respond faster, and grow your business.
          </p>
          <BookingModal>
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer">
              Book Your Free Consultation
            </Button>
          </BookingModal>
        </div>
      </section>
    </Layout>
  );
};

export default AIServices;
